import { gql, useMutation } from '@apollo/client';
import { Flex, HStack, useToast } from '@chakra-ui/react';
import PluginEditor from '@draft-js-plugins/editor';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { LogInSignUpModal } from '@/components/LogInSignUp';
import { PaywallModal } from '@/components/Paywall';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { useShowPostResponses } from '@/hooks/useShowPostResponses';
import { comment_created, analyzeContent } from '@/analytics';
import { CurrentUserAvatar } from '@/imports/ui/chakra/components/CurrentUserAvatar';
import { cleanEditorStateMentions } from '@/imports/ui/components/TextEditor/utils';

import { CreateComment_PostFragment } from './graphql-types/CreateComment_PostFragment';
import {
  CreateComment_createComment,
  CreateComment_createCommentVariables,
} from './graphql-types/CreateComment_createComment';

import { useSponsorPaywallModal } from '../../hooks/useSponsorPaywallModal';
import { CommentForm } from '../CommentForm';
import { Comments } from '../Comments';

const CREATE_COMMENT = gql`
  mutation CreateComment_createComment($input: CreateCommentInput_V2!) {
    createCommentV2(input: $input) {
      data {
        _id
        bodyContentState {
          ...RawDraftContentStateFragment
        }
        versionFiles {
          _id
          file {
            _id
            name
            isVideo
            isImage
            url
          }
        }
        postV2 {
          _id
          commentCount
          ...Comments_PostFragment
        }
      }
    }
  }
  ${RawDraftContentStateFragmentAST}
  ${Comments.postFragment}
`;

type CommentFormValues = {
  body: EditorState;
  images: ({ imageId: string } | { videoId: string })[];
  attachments: { versionFileId: string; fileName: string }[];
};

export const CreateComment = React.forwardRef<
  PluginEditor,
  {
    post: CreateComment_PostFragment;
    isActive: boolean;
    setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  }
>(({ post, isActive, setIsActive }, ref) => {
  const toast = useToast();
  const methods = useForm<CommentFormValues>({
    defaultValues: {
      images: [],
      attachments: [],
      body: EditorState.moveSelectionToEnd(
        cleanEditorStateMentions(
          EditorState.createWithContent(ContentState.createFromText(''))
        )
      ),
    },
  });

  const [createComment, { loading }] = useMutation<
    CreateComment_createComment,
    CreateComment_createCommentVariables
  >(CREATE_COMMENT, {
    onCompleted: (data) => {
      const commentBody = methods.getValues('body');
      const bodyText = commentBody.getCurrentContent().getPlainText();
      const contentAnalysis = analyzeContent(bodyText);

      methods.reset();
      /** `methods.reset()` does not work on `body` */
      methods.setValue(
        'body',
        EditorState.moveSelectionToEnd(
          cleanEditorStateMentions(
            EditorState.createWithContent(ContentState.createFromText(''))
          )
        )
      );
      setIsActive(false);

      comment_created({
        entity_type: 'comment',
        entity_id: data?.createCommentV2?.data?._id,
        location_posted_in: post.group?.isGlobalSingleton ? 'home' : 'group',
        location_group_id: post.group?._id,
        location_event_id: post.event?._id,
        location_organization_id: post.organization?._id,
        parent_id: post._id,
        parent_type: 'post',
        content_type: 'comment',
        content_text: contentAnalysis.contentText,
        content_length: contentAnalysis.contentLength,
        content_word_count: contentAnalysis.wordCount,
        content_has_question_mark: contentAnalysis.hasQuestionMark,
        content_has_links: contentAnalysis.hasLinks,
        content_link_count: contentAnalysis.linkCount,
        content_mention_count: contentAnalysis.mentionCount,
        content_hashtag_count: contentAnalysis.hashtagCount,
        content_hashtags: contentAnalysis.hashtags,
        post_type: post.__typename === 'AnswerV2'
          ? 'answer'
          : post.__typename === 'QuestionV2'
            ? 'question'
            : post.__typename === 'BlogV2'
              ? 'article'
              : post.__typename === 'EventAlert'
                ? 'eventPublished'
                : post.__typename === 'PollV2'
                  ? 'poll'
                  : 'message',
        post_is_reply: false,
      });
    },
    onError: () => {
      toast({
        status: 'error',
        description: 'Failed to create comment',
      });
    },
  });

  // @ts-ignore
  const editor = ref && ref.current;
  React.useEffect(() => {
    if (editor) {
      editor.focus();
    }
  }, [editor]);

  const sponsorPaywallModalProps = useSponsorPaywallModal(post);

  const {
    showPostResponses,
    behaviorOverride,
    paywallDisclosure,
    loginDisclosure,
  } = useShowPostResponses(post);

  if (!showPostResponses) {
    return null;
  }

  const createCommentHandler = async (params: CommentFormValues) => {
    await createComment({
      variables: {
        input: {
          data: {
            postId: post._id,
            body: convertToRaw(params.body.getCurrentContent()),
            versionFileIds: params.attachments.map(
              (attachment) => attachment.versionFileId
            ),
            embeddedImageIds: params.images
              .filter((id): id is { imageId: string } => 'imageId' in id)
              .map((id) => id.imageId),
          },
        },
      },
    });
  };

  const onClick = (): void => {
    if (behaviorOverride) {
      behaviorOverride();
    } else {
      setIsActive(true);
    }
  };

  return (
    <>
      <HStack alignItems="start" width="full">
        <CurrentUserAvatar width={10} height={10} />
        {isActive && post.currentUserCanRespondToPost ? (
          <FormProvider {...methods}>
            <CommentForm
              onSubmit={methods.handleSubmit(createCommentHandler)}
              ref={ref}
              submitButtonText="Comment"
              mentionData={{
                _id: post?.author._id,
                displayName: post?.author.displayName,
                postId: post?._id,
            }}
              onClickCancel={() => {
                methods.reset();
                // @ts-ignore
                ref?.current?.blur();
                setIsActive(false);
              }}
              isSubmitting={loading}
            />
          </FormProvider>
        ) : (
          <Flex
            onClick={onClick}
            borderRadius="full"
            width="full"
            backgroundColor="gray.50"
            alignItems="start"
            color="gray.400"
            py="2"
            px="4"
          >
            Write a comment...
          </Flex>
        )}
      </HStack>
      <PaywallModal
        isOpen={paywallDisclosure.isOpen}
        onClose={paywallDisclosure.onClose}
        source="respond to post paywall"
        {...sponsorPaywallModalProps}
      />
      <LogInSignUpModal
        isOpen={loginDisclosure.isOpen}
        onClose={loginDisclosure.onClose}
      />
    </>
  );
});

export const CreateComment_postFragment = gql`
  fragment CreateComment_PostFragment on PostV2 {
    _id
    ...useShowPostResponses_PostFragment
    ...useSponsorPaywallModal_PostFragment
    tags {
      _id
    }
    announcement {
      _id
    }
    group {
      _id
      isGlobalSingleton
    }
    organization {
      _id
    }
    event {
      _id
    }
    author {
      _id
      displayName
    }
  }
  ${useShowPostResponses.postFragment}
  ${useSponsorPaywallModal.postFragment}
`;

CreateComment.displayName = 'CreateComment';
